import React from "react";
import logo from "../../../assets/svg/auth/logo.svg";
import useCustomScrollbar from "../../../styles/useCustomScrollbar";
import "../../../styles/customScrollbar.css";

interface AuthLayoutProps {
  children: React.ReactNode;
  /** A wider column, for a screen whose rows do not read well at form width. */
  wide?: boolean;
  /**
   * Off for a screen that states the same terms itself - the consent screen
   * asks for them as explicit ticks, so the footer would say it twice and in
   * weaker words than the thing the user is actually agreeing to.
   */
  showFooter?: boolean;
  /**
   * On a phone, hand the column the whole height instead of centring it in the
   * height - so a screen can put its heading at the top and its action at the
   * bottom, with the air between them rather than split above and below.
   *
   * That is the shape a phone app's screens are built in, and the reason is
   * the thumb: the action is what the hand is reaching for, and on a 360x780
   * screen a centred column puts it in the middle, where the hand has to be
   * lifted to reach it. Centring is right on a desktop, where the column is a
   * card in a wide empty field and there is no reach to think about - so it is
   * what `sm:` up still gets, unchanged.
   *
   * Opt-in rather than the default, because it only pays for a screen that has
   * something to push down there. The screens that are a single block of text
   * or a spinner read better centred at any width.
   */
  phoneFill?: boolean;
  /**
   * Hand the air above the column back to the column, on a phone.
   *
   * `phoneFill` opens its screens with a band between the logo and the first
   * line, and on the screens it was written for - a log-in form, an OTP box -
   * that band is free: the content is short, and without it the action would
   * only sit further from the thumb. On a screen whose content fills the glass
   * it is not free, it is a row of terms.
   *
   * The consent screen is that screen. Its three terms are one grouped sheet
   * now rather than three cards, which is taller than what it replaced, and
   * with the band in place the last of the three - the one that has to be
   * ticked to get past the screen - ran under the pinned bar on a 390x844 and
   * off the bottom entirely on a 360x640.
   *
   * Opt-in, because it is a trade and not an improvement: the air buys reach
   * on a screen with room to spare, and only a screen with something below the
   * fold is better off spending it on the fold.
   */
  tightTop?: boolean;
}

/**
 * Shared shell for the auth screens: logo pinned top-left, the form column
 * centred in the viewport, legal copy pinned to the bottom.
 *
 * The screen scrolls inside itself rather than letting the document scroll,
 * which is what puts the app's own overlay scrollbar on it instead of the
 * platform's - the same one the signed-in shell uses. `overflow: auto` comes
 * with the class, so it still only appears when a screen is actually too tall.
 */
const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  wide = false,
  showFooter = true,
  phoneFill = false,
  tightTop = false,
}) => {
  // Reveals the thumb on hover, scroll and drag. Safe to call per auth screen:
  // it attaches once per element and cleans up on unmount.
  useCustomScrollbar();

  return (
    <div className="viewport-height bg-white custom-scrollbar-overlay">
      {/* `min-h-full`, not `min-h-screen`: the height to fill is the scroll
          container's, or a screen taller than the viewport would be measured
          against the viewport twice and leave a dead band below the footer. */}
      {/* On a phone the vertical padding is measured from the safe area rather
          than from the edge of the glass, so the logo clears the notch and the
          last line of the screen clears the home indicator. `env()` resolves
          to 0 where there are no insets, which leaves every other browser -
          and every sm-and-up screen, which sets its own padding - as it was.

          The bottom is 2.5rem on top of that inset, not the 1.5rem the top
          gets, because the inset is not the whole story down there: Android
          Chrome reports 0 for it while still drawing a gesture bar over the
          foot of the page, so a screen that pins its last line to the bottom
          - the log-in and sign-up screens both do - was handing that line to
          the navigation bar to cut. The extra 16px is what the inset would
          have been asked for had it been reported. The top keeps 1.5rem: a
          notch does report itself, and nothing is pinned up there anyway. */}
      {/* A brand wash behind the top of a phone screen, and nothing from sm up.

          A phone auth screen is a handful of controls on a field of white, and
          white to all four edges is what a page looks like - the app around it
          is the browser's chrome, which on a phone is barely there. The wash is
          what puts the product behind the screen instead: it is the brand's
          teal at a tint that text still reads over at AA, falling off to
          nothing above the first control, so it colours the screen without
          becoming a header the content has to clear.

          It falls over 280px, with a stop part way down rather than a straight
          run to nothing: a wash that ends abruptly reads as a band the content
          then crosses, and the crossing is what you notice. The stop is what
          lets it reach the field without the edge ever being a line.

          280px is where it stops, and that is not a look but a constraint: it
          has to be spent above the primary button on the shortest of these
          screens. A disabled primary is a pale tint of the same teal (see
          `Button`), so a wash still running underneath it would leave the two
          reading as one shape with no edge between them. Anything that moves
          the button up - a field removed, the heading shortened - moves this
          number with it.

          The cost of reaching that far is that it passes behind the primary
          button, and a disabled primary is pale teal on pale teal. That is why
          the button carries a hairline while it is off - see `LoginPage`.

          A background image rather than a positioned element, so it cannot take
          a tap, cannot be scrolled out of alignment with the padding, and costs
          no node. `bg-none` at sm returns the designed white page exactly. */}
      <div
        className={`min-h-full flex flex-col px-page pt-[calc(env(safe-area-inset-top)_+_theme(spacing.page-top))] pb-[calc(env(safe-area-inset-bottom)_+_2.5rem)] sm:px-12 sm:py-10 3xl:px-16 3xl:py-14 ${
          phoneFill
            ? "bg-[radial-gradient(125%_100%_at_50%_0%,#D2F4F7_0%,rgba(210,244,247,0.55)_45%,rgba(210,244,247,0)_100%)] bg-[length:100%_280px] bg-no-repeat sm:bg-none"
            : ""
        }`}
      >
        <header>
          <img src={logo} alt="Naurist" className="h-6 sm:h-6 3xl:h-7 w-auto" />
        </header>

        {/* The column is centred in whatever the logo leaves. The band around
            it is narrower on a phone: the keyboard takes half the height once
            a field is focused, and every row spent on air is one the form
            does not get. */}
        {/* Every `phoneFill` class below is undone at `sm:`, so a wide screen
            renders exactly what it did before the prop existed - the phone
            layout is the only thing it adds. */}
        <main
          className={`flex-1 flex justify-center ${
            phoneFill
              ? `items-stretch sm:items-center sm:py-10 ${
                  tightTop ? "pt-8" : "pt-20"
                }`
              : "items-center py-8 sm:py-10"
          }`}
        >
          <div
            className={`w-full ${
              phoneFill ? "flex flex-col sm:block" : ""
            } ${
              wide
                ? "max-w-[560px] 3xl:max-w-[620px]"
                : "max-w-[460px] 3xl:max-w-[520px]"
            }`}
          >
            {children}
          </div>
        </main>

        {/* {showFooter && (
          <footer className="mx-auto max-w-[480px] 3xl:max-w-[560px] text-center text-[12px] 3xl:text-[14px] leading-6 text-[#5A6672]">
            By clicking &ldquo;Log in&rdquo; or &ldquo;Continue with
            Google&rdquo; you agree to our{" "}
            <a href="/terms" className="text-[#00C2CB] hover:underline">
              Terms of Use
            </a>{" "}
            and acknowledge that you have read and understand our{" "}
            <a href="/privacy" className="text-[#00C2CB] hover:underline">
              Privacy Policy
            </a>
            .
          </footer>
        )} */}
      </div>
    </div>
  );
};

export default AuthLayout;
